import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionProductosGet, apiProductosInstance, authHeaderProductos } from "../../redux/actions/productos/productos";

import {
  Button,
  Card,
  Input,
  Select,
  Space,
  Typography,
  Row,
  Col,
  Skeleton,
  ConfigProvider,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const money = (n) =>
  n == null ? "—" : `$${parseFloat(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProductosPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.productos);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");
  const [categoriaFilter, setCategoriaFilter] = useState(null);

  const [categorias, setCategorias] = useState([]);

  const lastFetchKey = useRef("");

  useEffect(() => {
    const headers = authHeaderProductos();
    apiProductosInstance.get("/categorias", { headers })
      .then((r) => {
        const raw = r.data?.items ?? r.data ?? [];
        setCategorias(raw.map((c) => ({ label: c.nombre, value: c.id_categoria })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionProductosGet(fetchParams));
  }, [dispatch, fetchParams]);

  const baseFiltered = useMemo(() => {
    let base = items;
    if (categoriaFilter != null) {
      base = base.filter((p) => p.id_categoria === categoriaFilter);
    }
    return base;
  }, [items, categoriaFilter]);

  const counts = useMemo(() => ({
    total: baseFiltered.length,
    conVariantes: baseFiltered.filter((p) => (p.variantes_count ?? 0) > 0).length,
    stockBajo: baseFiltered.filter((p) => Boolean(p.stock_bajo)).length,
  }), [baseFiltered]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "con_variantes") return baseFiltered.filter((p) => (p.variantes_count ?? 0) > 0);
    if (activeFilter === "stock_bajo") return baseFiltered.filter((p) => Boolean(p.stock_bajo));
    return baseFiltered;
  }, [baseFiltered, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de productos", icon: <i className="fa-solid fa-compact-disc" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "con_variantes", label: "Con variantes", icon: <i className="fa-solid fa-list" />, iconCls: "paq-stat-icon-decoracion", count: counts.conVariantes },
    { key: "stock_bajo", label: "Stock bajo", icon: <i className="fa-solid fa-triangle-exclamation" />, iconCls: "paq-stat-icon-barra", count: counts.stockBajo },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Productos</Title>
                <Text className="paq-subtitle">Catalogo de productos y sus variantes (SKU, precio, existencia).</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24} lg={14}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre de producto..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
                <Col xs={24} lg={10}>
                  <div className="paq-field-label">Categoria</div>
                  <Select
                    value={categoriaFilter}
                    onChange={(v) => setCategoriaFilter(v ?? null)}
                    className="paq-control"
                    placeholder="Todas las categorias"
                    allowClear
                    options={categorias}
                    showSearch
                    optionFilterProp="label"
                  />
                </Col>
              </Row>

              <Row gutter={[16, 14]} style={{ marginTop: 14 }} align="bottom">
                <Col xs={24} lg={6}>
                  <div className="paq-actions">
                    <Button
                      className="paq-btn-clean"
                      onClick={() => {
                        setSearch("");
                        setActiveFilter("todos");
                        setCategoriaFilter(null);
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Productos ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} productos encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/productos/crear")}
                      className="custom-button"
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Nuevo producto
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin productos que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 8 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((p) => (
                        <div key={p.id_producto} className="paq-card">
                          <div
                            className="paq-card-banner"
                            style={{ background: BANNER_BG, flexDirection: "column", gap: 6 }}
                          >
                            <i className="fa-solid fa-compact-disc paq-card-banner-icon" />
                          </div>

                          <div className="paq-card-header">
                            <div className="paq-card-head">
                              <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>
                                {p.nombre}
                                {p.artista ? <span style={{ display: "block", fontSize: 12, fontWeight: 400, color: "#7a7f87" }}>{p.artista}</span> : null}
                              </Paragraph>
                              <span className="paq-tipo-badge paq-tipo-sonido">
                                {p.precio_desde != null ? `Desde ${money(p.precio_desde)}` : "Sin precio"}
                              </span>
                            </div>
                          </div>

                          <div className="paq-card-body">
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-tags paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Categoria</span>
                                <span className="paq-info-value">{p.categoria_nombre || "Sin categoria"}</span>
                              </div>
                            </div>
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-list paq-info-icon paq-info-icon-receta" />
                              <div className="paq-info-text">
                                <span className="paq-info-label paq-info-label-receta">Variantes</span>
                                <span className="paq-info-value">
                                  {p.variantes_count ?? 0} {p.variantes_count === 1 ? "variante" : "variantes"}
                                  {p.existencia_total != null ? ` · ${parseFloat(p.existencia_total)} en existencia` : ""}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="paq-card-footer">
                            {Boolean(p.stock_bajo) && (
                              <div className="paq-bottom-strip is-red">
                                <span className="paq-strip-label">Stock bajo</span>
                              </div>
                            )}
                            <Button
                              className="paq-btn-details"
                              icon={<i className="fa-solid fa-circle-info" />}
                              onClick={() => navigate(`/app/productos/${p.id_producto}`)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
